import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import i18n from '../i18n';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Глобален error boundary за целия App.
 *
 * Без него, при непремахната JS грешка по време на render, React 18
 * размонтира ЦЯЛОТО дърво -> целият екран става бял, без индикация какво
 * се е случило (наблюдавано на iOS Safari/Chrome след stale-cache зареждане
 * на стар JS бъндъл). Тук поне показваме съобщение + бутон за презареждане,
 * вместо тих бял екран.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Uncaught render error caught by ErrorBoundary:', error, info);
  }

  handleReload = () => {
    // Твърдо презареждане (без кеш) - оправя случая, при който
    // проблемът е bил стар/кеширан JS бъндъл.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h1">
              {i18n.t('app_crash.title')}
            </Typography>
            <Typography color="text.secondary">
              {i18n.t('app_crash.message')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
            >
              {i18n.t('app_crash.reload_button')}
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
